import React, { useState, useEffect } from 'react';

/**
 * Dashboard Navigation Component
 * Mobile-first bottom navigation bar with top tabs for desktop
 * Clean City Belmopan - Simplified navigation
 */
const DashboardNavigation = ({ activeTab, setActiveTab, userVehicles = [], sharedVehicles = [], currentUser = null }) => {
  const [enabledTabs, setEnabledTabs] = useState({
    map: true,
    'enhanced-subs': true,
    schedules: true,
    subscriptions: true,
    vehicles: false,
    shared: false
  });

  // Load tab settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('userTabSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setEnabledTabs(parsed);
      } catch (error) {
      }
    }
  }, []);

  // Listen for settings updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userTabSettings' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setEnabledTabs(parsed);
        } catch (error) {
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for direct updates from the settings modal
    const handleCustomEvent = (e) => {
      setEnabledTabs(e.detail);
    };

    window.addEventListener('tabSettingsUpdated', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tabSettingsUpdated', handleCustomEvent);
    };
  }, []);

  // Primary tabs for bottom navigation (mobile)
  const primaryTabs = [
    { id: 'map', label: 'Map', icon: '🗺️' },
    { id: 'enhanced-subs', label: 'Alerts', icon: '🔔' },
    { id: 'schedules', label: 'Schedules', icon: '📅' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '⭐' }
  ];

  // Secondary tabs (shown in "More" menu or only on desktop)
  const secondaryTabs = [
    ...(userVehicles.length > 0 ? [{ id: 'vehicles', label: 'My Trucks', icon: '🚛', count: userVehicles.length }] : []),
    ...(sharedVehicles.length > 0 ? [{ id: 'shared', label: 'Shared', icon: '🤝', count: sharedVehicles.length }] : [])
  ];

  const allTabs = [...primaryTabs, ...secondaryTabs];

  // Filter tabs based on user settings - always show vehicles tab if user has vehicles
  const visibleTabs = allTabs.filter(tab => {
    if (tab.id === 'vehicles' && userVehicles.length > 0) {
      return true;
    }
    if (tab.id === 'shared' && sharedVehicles.length > 0) {
      return true;
    }
    return enabledTabs[tab.id];
  });

  // If current active tab is disabled, switch to map
  useEffect(() => {
    const isVehiclesTabWithVehicles = activeTab === 'vehicles' && userVehicles.length > 0;
    const isSharedTabWithShared = activeTab === 'shared' && sharedVehicles.length > 0;
    if (!enabledTabs[activeTab] && !isVehiclesTabWithVehicles && !isSharedTabWithShared) {
      setActiveTab('map');
    }
  }, [enabledTabs, activeTab, setActiveTab, userVehicles.length, sharedVehicles.length]);

  // Tabs visible in bottom nav (max 5 items)
  const bottomNavTabs = visibleTabs.slice(0, 5);

  return (
    <>
      {/* Desktop Navigation - Top tabs (hidden on mobile) */}
      <nav className="hidden md:block bg-gradient-to-r from-blue-50 to-cyan-50 shadow-sm border-b border-blue-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex overflow-x-auto scrollbar-hide px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-4 lg:space-x-8 min-w-max">
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center py-4 px-4 border-b-3 font-medium text-sm transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-700 bg-white/60'
                      : 'border-transparent text-blue-600/70 hover:text-blue-700 hover:border-blue-300 hover:bg-white/40'
                  }`}
                >
                  <span className="text-lg mr-2">{tab.icon}</span>
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className="ml-2 bg-blue-100 text-blue-700 py-0.5 px-2 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Bottom bar (hidden on desktop) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[2000] bg-white border-t border-gray-200 shadow-lg safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {bottomNavTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center flex-1 h-full py-1 px-1 transition-all relative ${
                activeTab === tab.id
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-blue-500'
              }`}
            >
              {/* Active indicator */}
              {activeTab === tab.id && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-600 rounded-b-full" />
              )}

              <span className={`text-xl mb-0.5 ${activeTab === tab.id ? 'transform scale-110' : ''}`}>
                {tab.icon}
              </span>
              <span className={`text-xs font-medium truncate max-w-full ${
                activeTab === tab.id ? 'text-blue-600' : 'text-gray-600'
              }`}>
                {tab.label}
              </span>

              {/* Badge for counts */}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="absolute top-1 right-1/4 bg-blue-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};

export default DashboardNavigation;
